import React, { useContext, useState, useEffect, useRef } from 'react'
import {
  Box,
  Grid,
  Card,
  CardContent,
  Typography,
  Tabs,
  Tab,
  Table,
  TableHead,
  TableRow,
  TableCell,
  TableBody,
  CircularProgress,
  LinearProgress,
  Chip,
  Alert,
  Button,
  Tooltip,
  FormControl,
  InputLabel,
  Select,
  MenuItem
} from '@mui/material'
import RefreshIcon from '@mui/icons-material/Refresh'
import ErrorOutlineIcon from '@mui/icons-material/ErrorOutline'
import CheckCircleIcon from '@mui/icons-material/CheckCircle'
import WarningAmberIcon from '@mui/icons-material/WarningAmber'
import CancelIcon from '@mui/icons-material/Cancel'
import PlayArrowIcon from '@mui/icons-material/PlayArrow'
import PauseIcon from '@mui/icons-material/Pause'
import TimerIcon from '@mui/icons-material/Timer'
import BlockIcon from '@mui/icons-material/Block'
import * as signalR from '@microsoft/signalr'
import { HomeAppContext } from '../Home'
import api from '../../Services/api'
import { errorManagement } from '../../Services/errorManagement'
import { styled } from '@mui/material/styles'

const StatsCard = styled(Card)(({ theme }) => ({
  height: '100%',
  display: 'flex',
  flexDirection: 'column',
  justifyContent: 'space-between'
}))

const formatDuration = ms => {
  if (ms === null || ms === undefined || Number.isNaN(ms)) return '-'
  if (ms < 1) return '0 ms'
  if (ms < 1000) return `${Math.round(ms)} ms`
  const seconds = ms / 1000
  if (seconds < 60) return `${seconds >= 10 ? Math.round(seconds) : seconds.toFixed(1)} s`
  const minutes = seconds / 60
  if (minutes < 60) return `${minutes >= 10 ? Math.round(minutes) : minutes.toFixed(1)} min`
  const hours = minutes / 60
  return `${hours >= 10 ? Math.round(hours) : hours.toFixed(1)} h`
}

const formatDelta = delta => {
  if (delta === null || delta === undefined || Number.isNaN(delta)) return '-'
  const sign = delta >= 0 ? '+' : ''
  return `${sign}${delta.toFixed(1)}%`
}

const safePercent = (value, max) => {
  if (!max || Number.isNaN(max)) return 0
  const pct = (value / max) * 100
  if (!Number.isFinite(pct)) return 0
  return Math.max(0, Math.min(100, pct))
}

const getDeltaColor = delta => {
  if (delta === null || delta === undefined || Number.isNaN(delta)) return 'default'
  if (delta >= 150) return 'error'
  if (delta >= 50) return 'warning'
  if (delta >= 0) return 'success'
  return 'info'
}

export default function AIAnalytics() {
  const { homeDispatch } = useContext(HomeAppContext)
  const [data, setData] = useState({
    engines: {},
    active: {},
    workerStatus: [],
    rateLimitStates: {},
    timestamp: null
  })
  const [error, setError] = useState('')
  const [initializing, setInitializing] = useState(true)
  const [refreshing, setRefreshing] = useState(false)
  const [tab, setTab] = useState(0)
  const [rateLimitFilter, setRateLimitFilter] = useState('all')
  const hubRef = useRef(null)

  useEffect(() => {
    homeDispatch({ type: 'UPDATE_CURRENT_LOCATION', data: 'AI Analytics' })
  }, [homeDispatch])

  const fetchOverview = async (initial = false) => {
    if (initial) setInitializing(true)
    else setRefreshing(true)
    try {
      const response = await api.get('/api/ai-analytics/overview')
      const overview = response.data
      setData({
        engines: overview.engineStatistics || {},
        active: overview.activeRequestCounts || {},
        workerStatus: overview.workerStatus || [],
        rateLimitStates: overview.rateLimitStates || {},
        timestamp: overview.timestamp
      })
      setError('')
    } catch (err) {
      setError('Failed to load analytics')
      errorManagement.formErrors(err, homeDispatch)
    } finally {
      if (initial) setInitializing(false)
      else setRefreshing(false)
    }
  }

  useEffect(() => {
    const conn = new signalR.HubConnectionBuilder()
      .withUrl(`${process.env.REACT_APP_BACKEND_URL}/aianalyticshub`)
      .withAutomaticReconnect()
      .build()
    hubRef.current = conn
    conn
      .start()
      .then(() => {
        conn.on('ReceiveAnalyticsOverview', overview => {
          setData({
            engines: overview.engineStatistics || {},
            active: overview.activeRequestCounts || {},
            workerStatus: overview.workerStatus || [],
            rateLimitStates: overview.rateLimitStates || {},
            timestamp: overview.timestamp
          })
        })
        conn.invoke('RequestAnalyticsOverview')
      })
      .catch(() => fetchOverview(true))
    return () => {
      conn.stop()
    }
  }, [])

  useEffect(() => {
    fetchOverview(true)
  }, [])

  useEffect(() => {
    const id = setInterval(() => {
      const hc = hubRef.current
      if (hc && hc.state === signalR.HubConnectionState.Connected) {
        hc.invoke('RequestAnalyticsOverview').catch(() => fetchOverview(false))
      } else {
        fetchOverview(false)
      }
    }, 5000)
    return () => clearInterval(id)
  }, [])

  if (error) {
    return (
      <Box p={4} textAlign="center">
        <ErrorOutlineIcon color="error" fontSize="large" />
        <Typography variant="h6" color="error" gutterBottom>
          {error}
        </Typography>
        <Button onClick={() => fetchOverview(true)} startIcon={<RefreshIcon />}>
          Retry
        </Button>
      </Box>
    )
  }

  if (initializing) {
    return (
      <Box p={4} display="flex" justifyContent="center" alignItems="center">
        <CircularProgress />
      </Box>
    )
  }

  const engines = Object.values(data.engines)
  const totalEngines = engines.length
  const totalActive = Object.values(data.active).reduce((sum, c) => sum + c, 0)
  const rateLimitedCount = engines.filter(e => e.isRateLimited).length

  const workerCounts = {
    total: data.workerStatus.length,
    processing: data.workerStatus.filter(w => w.state === 'Processing').length,
    idle: data.workerStatus.filter(w => w.state === 'Idle').length
  }

  const rateLimitEntries = Object.entries(data.rateLimitStates).map(([key, state]) => {
    const [provider, engineId] = key.split(':')
    return {
      key,
      provider,
      engineId: parseInt(engineId, 10),
      engineName: data.engines[engineId]?.engineName || `Engine ${engineId}`,
      expiration: new Date(state.expiration),
      retryAfterSeconds: state.retryAfterSeconds
    }
  })

  const providers = [...new Set(rateLimitEntries.map(entry => entry.provider))]
  const providerStats = providers.reduce((acc, provider) => {
    const providerEntries = rateLimitEntries.filter(entry => entry.provider === provider)
    acc[provider] = {
      totalRateLimits: providerEntries.length,
      maxRetrySeconds: Math.max(...providerEntries.map(e => e.retryAfterSeconds), 0)
    }
    return acc
  }, {})

  const filteredRateLimits =
    rateLimitFilter === 'all'
      ? rateLimitEntries
      : rateLimitEntries.filter(entry => entry.provider === rateLimitFilter)

  const enginesWithLatency = engines.filter(
    engine => typeof engine.recentExecutionMs === 'number' && engine.recentExecutionMs >= 0
  )
  const avgRecentLatency =
    enginesWithLatency.length > 0
      ? enginesWithLatency.reduce((sum, engine) => sum + engine.recentExecutionMs, 0) / enginesWithLatency.length
      : null
  const avgBaselineLatency =
    enginesWithLatency.length > 0
      ? enginesWithLatency.reduce((sum, engine) => sum + (engine.baselineExecutionMs ?? engine.recentExecutionMs), 0) /
        enginesWithLatency.length
      : null
  const queueLatencySamples = enginesWithLatency.filter(
    engine => typeof engine.averageQueueMs === 'number' && engine.averageQueueMs >= 0
  )
  const avgQueueLatency =
    queueLatencySamples.length > 0
      ? queueLatencySamples.reduce((sum, engine) => sum + engine.averageQueueMs, 0) / queueLatencySamples.length
      : null
  const totalLatencyAlerts = engines.filter(engine => engine.latencyAlert).length
  const highDeltaEngines = enginesWithLatency.filter(
    engine => typeof engine.latencyDeltaPercent === 'number' && engine.latencyDeltaPercent > 50
  ).length

  let systemStatus = 'success'
  if (totalLatencyAlerts > 0) systemStatus = 'error'
  else if (rateLimitedCount > 0 || rateLimitEntries.length > 0) systemStatus = 'error'
  else if (totalActive > totalEngines || highDeltaEngines > 0) systemStatus = 'warning'

  const getWorkerStateIcon = state => {
    switch (state) {
      case 'Processing':
        return <PlayArrowIcon fontSize="small" />
      case 'Idle':
        return <PauseIcon fontSize="small" />
      default:
        return <WarningAmberIcon fontSize="small" />
    }
  }

  const getWorkerStateColor = state => {
    switch (state) {
      case 'Processing':
        return 'primary'
      case 'Idle':
        return 'default'
      default:
        return 'warning'
    }
  }

  const getTimeRemaining = expiration => {
    const remainingMs = expiration - Date.now()
    if (remainingMs <= 0) return 'Expired'
    const seconds = Math.floor(remainingMs / 1000) % 60
    const minutes = Math.floor(remainingMs / (1000 * 60)) % 60
    return `${minutes}m ${seconds}s`
  }

  return (
    <Box p={4}>
      <Box display="flex" justifyContent="space-between" alignItems="center" mb={3}>
        <Typography variant="h5">AI System Analytics</Typography>
      </Box>
      <Box display="flex" alignItems="center" mb={2}>
        <Typography variant="body2" color="textSecondary">
          Last updated: {data.timestamp && new Date(data.timestamp).toLocaleString()}
        </Typography>
        {refreshing && <CircularProgress size={16} sx={{ ml: 1 }} />}
      </Box>
      <Grid container spacing={3} sx={{ mb: 3 }}>
        <Grid item xs={12} md={3}>
          <StatsCard>
            <CardContent>
              <Typography variant="h6" gutterBottom>
                System Status
              </Typography>
              <Chip
                label={
                  systemStatus === 'success'
                    ? 'Healthy'
                    : systemStatus === 'warning'
                    ? 'Degraded'
                    : 'Issues Detected'
                }
                icon={
                  systemStatus === 'success' ? (
                    <CheckCircleIcon />
                  ) : systemStatus === 'warning' ? (
                    <WarningAmberIcon />
                  ) : (
                    <CancelIcon />
                  )
                }
                color={
                  systemStatus === 'success'
                    ? 'success'
                    : systemStatus === 'warning'
                    ? 'warning'
                    : 'error'
                }
              />
              {avgRecentLatency !== null && (
                <Box mt={2}>
                  <Typography variant="body2" color="text.secondary">
                    Recent avg: {formatDuration(avgRecentLatency)}
                  </Typography>
                  {avgBaselineLatency !== null && (
                    <Typography variant="body2" color="text.secondary">
                      Baseline: {formatDuration(avgBaselineLatency)}
                    </Typography>
                  )}
                  {avgQueueLatency !== null && (
                    <Typography variant="body2" color="text.secondary">
                      Avg queue: {formatDuration(avgQueueLatency)}
                    </Typography>
                  )}
                  <Typography variant="body2" color={totalLatencyAlerts > 0 ? 'error' : 'success.main'} mt={1}>
                    {totalLatencyAlerts > 0
                      ? `${totalLatencyAlerts} engine${totalLatencyAlerts > 1 ? 's' : ''} with latency alerts`
                      : 'No latency alerts'}
                  </Typography>
                </Box>
              )}
            </CardContent>
          </StatsCard>
        </Grid>
        <Grid item xs={12} md={3}>
          <StatsCard>
            <CardContent>
              <Typography variant="h6" gutterBottom>
                Engines
              </Typography>
              <Typography variant="h4">{totalEngines}</Typography>
              {rateLimitedCount > 0 && <Typography color="error">{rateLimitedCount} rate limited</Typography>}
              {totalLatencyAlerts > 0 ? (
                <Typography color="error">{totalLatencyAlerts} latency alerts</Typography>
              ) : (
                avgRecentLatency !== null && (
                  <Typography color="text.secondary">Avg latency {formatDuration(avgRecentLatency)}</Typography>
                )
              )}
            </CardContent>
          </StatsCard>
        </Grid>
        <Grid item xs={12} md={3}>
          <StatsCard>
            <CardContent>
              <Typography variant="h6" gutterBottom>
                Active Requests
              </Typography>
              <Typography variant="h4">{totalActive}</Typography>
              {avgQueueLatency !== null && (
                <Typography color="text.secondary">Avg queue {formatDuration(avgQueueLatency)}</Typography>
              )}
            </CardContent>
          </StatsCard>
        </Grid>
        <Grid item xs={12} md={3}>
          <StatsCard>
            <CardContent>
              <Typography variant="h6" gutterBottom>
                Workers
              </Typography>
              <Typography variant="h4">{workerCounts.total}</Typography>
              <Box display="flex" gap={1} mt={1}>
                <Chip size="small" icon={<PlayArrowIcon />} label={`${workerCounts.processing} active`} color="primary" />
              </Box>
            </CardContent>
          </StatsCard>
        </Grid>
      </Grid>
      <Tabs
        value={tab}
        onChange={(_, v) => setTab(v)}
        indicatorColor="primary"
        textColor="primary"
        sx={{ borderBottom: 1, borderColor: 'divider', mb: 2 }}
      >
        <Tab label="Engines" />
        <Tab label="Active Requests" />
        <Tab label="Worker Status" />
        <Tab label="Rate Limits" />
      </Tabs>
      {tab === 0 && (
        <Card>
          <CardContent>
            <Typography variant="h6" gutterBottom>
              Engines Overview
            </Typography>
            <Table size="small">
              <TableHead>
                <TableRow>
                  <TableCell>Engine</TableCell>
                  <TableCell>Provider</TableCell>
                  <TableCell>Status</TableCell>
                  <TableCell>Tokens</TableCell>
                  <TableCell>Calls</TableCell>
                  <TableCell>Latency (Recent / Baseline)</TableCell>
                  <TableCell>Δ%</TableCell>
                  <TableCell>Avg Queue</TableCell>
                  <TableCell>Recommendation</TableCell>
                </TableRow>
              </TableHead>
              <TableBody>
                {engines.map(engine => {
                  const recent = formatDuration(engine.recentExecutionMs)
                  const baseline = formatDuration(engine.baselineExecutionMs)
                  const queue = formatDuration(engine.averageQueueMs)
                  const delta = engine.latencyDeltaPercent
                  const deltaLabel = formatDelta(delta)
                  const deltaColor = getDeltaColor(delta)
                  return (
                    <TableRow
                      key={engine.engineId}
                      hover
                      sx={{ backgroundColor: engine.latencyAlert ? 'rgba(255, 152, 0, 0.08)' : 'inherit' }}
                    >
                      <TableCell>{engine.engineName}</TableCell>
                      <TableCell>{engine.providerName}</TableCell>
                      <TableCell>
                        {engine.isRateLimited ? (
                          <Chip icon={<CancelIcon />} label="Rate Limited" color="error" size="small" />
                        ) : (
                          <Chip icon={<CheckCircleIcon />} label="Available" color="success" size="small" />
                        )}
                      </TableCell>
                      <TableCell>
                        {`${engine.remainingTokens} / ${engine.maxTokens}`}
                        <LinearProgress sx={{ mt: 0.5 }} variant="determinate" value={safePercent(engine.remainingTokens, engine.maxTokens)} />
                      </TableCell>
                      <TableCell>
                        {`${engine.remainingCallsPerMinute} / ${engine.maxCallsPerMinute}`}
                        <LinearProgress
                          sx={{ mt: 0.5 }}
                          variant="determinate"
                          value={safePercent(engine.remainingCallsPerMinute, engine.maxCallsPerMinute)}
                        />
                      </TableCell>
                      <TableCell>
                        <Typography variant="body2">{recent}</Typography>
                        {baseline !== '-' && (
                          <Typography variant="caption" color="text.secondary">
                            baseline {baseline}
                          </Typography>
                        )}
                      </TableCell>
                      <TableCell>
                        {delta === null || delta === undefined || Number.isNaN(delta) ? (
                          '-'
                        ) : (
                          <Chip
                            size="small"
                            label={deltaLabel}
                            color={deltaColor}
                            variant={engine.latencyAlert ? 'filled' : 'outlined'}
                          />
                        )}
                      </TableCell>
                      <TableCell>{queue}</TableCell>
                      <TableCell>
                        {engine.recommendedAction === 'scale-out' ? (
                          <Chip size="small" color="error" icon={<PlayArrowIcon />} label="Scale out" />
                        ) : engine.recommendedAction === 'throttle' ? (
                          <Chip size="small" color="warning" icon={<TimerIcon />} label="Throttle" />
                        ) : engine.latencyAlert ? (
                          <Chip size="small" color="warning" icon={<WarningAmberIcon />} label="Investigate" />
                        ) : (
                          '-'
                        )}
                      </TableCell>
                    </TableRow>
                  )
                })}
              </TableBody>
            </Table>
          </CardContent>
        </Card>
      )}
      {tab === 1 && (
        <Card>
          <CardContent>
            <Typography variant="h6" gutterBottom>
              Active Requests by Engine
            </Typography>
            {Object.keys(data.active).length > 0 ? (
              <Table size="small">
                <TableHead>
                  <TableRow>
                    <TableCell>Engine</TableCell>
                    <TableCell>Active Requests</TableCell>
                  </TableRow>
                </TableHead>
                <TableBody>
                  {Object.entries(data.active).map(([id, count]) => (
                    <TableRow key={id} hover>
                      <TableCell>{data.engines[id]?.engineName || id}</TableCell>
                      <TableCell>{count}</TableCell>
                    </TableRow>
                  ))}
                </TableBody>
              </Table>
            ) : (
              <Alert severity="info">No active requests currently</Alert>
            )}
          </CardContent>
        </Card>
      )}
      {tab === 2 && (
        <Card>
          <CardContent>
            <Typography variant="h6" gutterBottom>
              Worker Status
            </Typography>
            {data.workerStatus && data.workerStatus.length > 0 ? (
              <Table size="small">
                <TableHead>
                  <TableRow>
                    <TableCell>Worker ID</TableCell>
                    <TableCell>Provider</TableCell>
                    <TableCell>State</TableCell>
                    <TableCell>Engine</TableCell>
                    <TableCell>Last Updated</TableCell>
                    <TableCell>Details</TableCell>
                  </TableRow>
                </TableHead>
                <TableBody>
                  {data.workerStatus.map(worker => (
                    <TableRow key={worker.workerId} hover sx={{ backgroundColor: 'inherit' }}>
                      <TableCell>{worker.workerId}</TableCell>
                      <TableCell>{worker.provider}</TableCell>
                      <TableCell>
                        <Chip icon={getWorkerStateIcon(worker.state)} label={worker.state} color={getWorkerStateColor(worker.state)} size="small" />
                      </TableCell>
                      <TableCell>{worker.engineId > 0 ? worker.engineName : '-'}</TableCell>
                      <TableCell>
                        <Tooltip title={new Date(worker.lastUpdated).toLocaleString()}>
                          <span>{Math.round((Date.now() - new Date(worker.lastUpdated)) / 1000)}s ago</span>
                        </Tooltip>
                      </TableCell>
                      <TableCell>
                        {worker.lastError ? (
                          <Tooltip title={worker.lastError}>
                            <ErrorOutlineIcon fontSize="small" color="error" />
                          </Tooltip>
                        ) : worker.state === 'Processing' ? (
                          worker.engineName || 'Processing'
                        ) : (
                          '-'
                        )}
                      </TableCell>
                    </TableRow>
                  ))}
                </TableBody>
              </Table>
            ) : (
              <Alert severity="info">No worker status information available</Alert>
            )}
          </CardContent>
        </Card>
      )}
      {tab === 3 && (
        <Card>
          <CardContent>
            <Box display="flex" justifyContent="space-between" alignItems="center" mb={2}>
              <Typography variant="h6">Rate Limit Status</Typography>
              <FormControl size="small" sx={{ width: 200 }}>
                <InputLabel>Filter by Provider</InputLabel>
                <Select value={rateLimitFilter} onChange={e => setRateLimitFilter(e.target.value)} label="Filter by Provider">
                  <MenuItem value="all">All Providers</MenuItem>
                  {providers.map(provider => (
                    <MenuItem key={provider} value={provider}>
                      {provider} ({providerStats[provider].totalRateLimits})
                    </MenuItem>
                  ))}
                </Select>
              </FormControl>
            </Box>
            {rateLimitEntries.length > 0 ? (
              <>
                {rateLimitFilter === 'all' && (
                  <Grid container spacing={2} sx={{ mb: 3 }}>
                    {providers.map(provider => (
                      <Grid item xs={12} md={4} key={provider}>
                        <Card variant="outlined">
                          <CardContent>
                            <Typography variant="h6" color="text.secondary" gutterBottom>
                              {provider}
                            </Typography>
                            <Typography variant="body1">
                              <BlockIcon fontSize="small" color="error" sx={{ mr: 1, verticalAlign: 'middle' }} />
                              {providerStats[provider].totalRateLimits} rate limited engines
                            </Typography>
                            <Typography variant="body1">
                              <TimerIcon fontSize="small" color="warning" sx={{ mr: 1, verticalAlign: 'middle' }} />
                              Max wait: {providerStats[provider].maxRetrySeconds}s
                            </Typography>
                          </CardContent>
                        </Card>
                      </Grid>
                    ))}
                  </Grid>
                )}
                <Table size="small">
                  <TableHead>
                    <TableRow>
                      <TableCell>Provider</TableCell>
                      <TableCell>Engine</TableCell>
                      <TableCell>Retry After</TableCell>
                      <TableCell>Time Remaining</TableCell>
                      <TableCell>Expiration</TableCell>
                    </TableRow>
                  </TableHead>
                  <TableBody>
                    {filteredRateLimits.map(entry => (
                      <TableRow key={entry.key} hover>
                        <TableCell>{entry.provider}</TableCell>
                        <TableCell>{entry.engineName}</TableCell>
                        <TableCell>{entry.retryAfterSeconds}s</TableCell>
                        <TableCell>
                          <Chip icon={<TimerIcon />} label={getTimeRemaining(entry.expiration)} color="warning" size="small" />
                        </TableCell>
                        <TableCell>
                          <Tooltip title={entry.expiration.toLocaleString()}>
                            <span>{entry.expiration.toLocaleTimeString()}</span>
                          </Tooltip>
                        </TableCell>
                      </TableRow>
                    ))}
                  </TableBody>
                </Table>
              </>
            ) : (
              <Alert severity="success">No rate limits currently active</Alert>
            )}
          </CardContent>
        </Card>
      )}
    </Box>
  )
}
